import { Accessor, JSX, Match, Setter, Show, Switch } from "solid-js";
import {
  AmountUnit,
  OfferConstruct,
  VoucherProduct,
} from "~/server/types/brand";
import { Divider, DividerV2 } from "~/widgets/divider";
import { Spacer } from "~/widgets/spacer";
import { DividerWithOfferConstruct } from "./denomination_grid_fragments/divider_with_offer_construct";
import { Variants } from "./denomination_grid_fragments/variants";
import {
  MultipleSubscriptionInfo,
  SingleSubscriptionInfo,
} from "./denomination_grid_fragments/subscription_info";
import CoinEarningInformation from "./denomination_grid_fragments/coin_earning_information";
import {
  CoinConsumptionInformation,
  CoinsOnlyConsumptionInfo,
} from "./denomination_grid_fragments/coin_consumption_information";
import { ProceedButton } from "./denomination_grid_fragments/proceed_button";
import { TransactionAdditionalInfo } from "./denomination_grid_fragments/transaction_additional_info";
import { Denominations } from "./denomination_grid_fragments/denominations";
import { CustomBlock } from "./denomination_grid_fragments/custom_block";
import { CustomAmountInput } from "./denomination_grid_fragments/custom_amount_input";
import { DenominationsHeader } from "./denomination_grid_fragments/denominations_header";
import useDenominationsGridDeprecated from "~/hooks/denominations_grid/useDenominationsGrid";
import { InsufficientCoins } from "./denomination_grid_fragments/insufficient_coins";
import { DueAmount } from "./denomination_grid_fragments/due_amount";
import { DenominationGridProvider } from "./context/denomination_grid_context";
import {
  InitialSimulationData,
  SimulateFunction,
} from "~/hooks/denominations_grid/useDenominationGridState";
import { toRupees } from "~/utils/number";
import { Mode } from "~/types";

export type CtaState = "DEFAULT" | "LOADING" | "DISABLED";

export type DenominationGridInternalState = {
  selectedAmount: () => number | null;
  selectedVariantId: () => string | null;
  rewardDiscountAmount: () => number;
  dueAmount: () => number | null;
  discountDetails: () => any;
  redemptionAmount: () => number | null;
  dueAmount2: () => number | null;
  discountDetails2: () => any;
  redemptionAmount2: () => number | null;
  offerConstructTenure: () => number | null;
  ctaState: () => CtaState;
  isSimulating: () => boolean;
  useRewardCoins: () => boolean | null;
  hubbleCoinRewards: () => any;
  initialErrorOnSimulate: () => string | null;
  dryRunErrorMessage: () => string | null;
  voucherCount: () => number;
};

export type DenominationProps = {
  discountPercentage: number;
  brand: VoucherProduct;
  isCoinsOnly: boolean;
  allowRewardCoins: boolean;
  showHubbleCoinRewards: boolean;
  coinsBalance: number;
  isRewardTypePostPaid: boolean;
  offerConstructMaxAmount?: number;
  offerConstructAmountUnit?: AmountUnit;
  offerConstruct?: OfferConstruct;
  minAmount: number;
  maxAmount: number;
  /** Initial simulation data from server */
  initialSimulationData: InitialSimulationData;
  /** Function to get current mode */
  getMode: () => Mode;

  /** Function to get client ID */
  getClientId: () => string | undefined;

  /** Function to simulate transactions */
  simulateFunction: SimulateFunction;

  /** Function to initiate order - now receives internal state */
  initiateOrder: (internalState: DenominationGridInternalState) => void;

  class?: string;
  dividerClass?: string;
  isDarkTheme?: boolean;

  /** if provided, will override the default header component */
  buildHeader?: () => JSX.Element;

  /** if provided, will override the default insufficient coins component */
  buildInsufficientCoins?: () => JSX.Element;

  /** if provided, will override the default cta copy */
  ctaCopy?: string;

  /** if provided, will override the default denomination list */
  denominationList?: number[];

  ctaState?: Accessor<CtaState>;
  setCtaState?: Setter<CtaState>;
};

export function DenominationsGrid(props: DenominationProps) {
  const gridState = useDenominationsGridDeprecated(props);
  const internalState = gridState.internalState;

  return (
    <DenominationGridProvider value={gridState}>
      <div
        class={`${props.class} shadow-sm  flex h-fit w-full flex-col items-center justify-start gap-4 rounded-2xl border border-baseTertiaryDark bg-baseTertiaryLight pb-4 dark:border-basePrimaryMedium dark:bg-baseDark lg:pb-6 `}
      >
        {props.buildHeader?.() ?? (
          <>
            <div class="mt-1 "></div>
            <DenominationsHeader
              brandTitle={props.brand.voucherProductMetadata.title}
              brandLogoUrl={props.brand.voucherProductMetadata.logoUrl}
            />
          </>
        )}
        <div class="w-full px-4 lg:px-6">
          <Switch>
            <Match when={gridState.isVariantBrand}>
              <div
                class="grid-rows-auto grid h-fit w-full  gap-3"
                style={{
                  "grid-template-columns": `repeat(${1}, 1fr)`,
                }}
              >
                <Variants />
              </div>
            </Match>
            <Match when={true}>
              <div
                id="denominations-container"
                class="grid-rows-auto grid h-fit w-full  gap-3"
                style={{
                  "grid-template-columns": `repeat(${gridState.columnsCount}, 1fr)`,
                }}
              >
                <Denominations />
                <Show when={gridState.isCustomDenominationAllowed}>
                  <CustomBlock />
                </Show>
              </div>
              <Spacer height={12} />
              <Show
                when={
                  gridState.isCustomDenominationAllowed &&
                  gridState.customSelected()
                }
              >
                <CustomAmountInput />
                <Spacer height={4} />
              </Show>
              <Show
                when={
                  internalState.voucherCount() > 1 &&
                  internalState.dryRunErrorMessage() == null
                }
              >
                <p class="text-medium text-textDark dark:text-white">
                  {internalState.voucherCount() || 0} cards worth{" "}
                  {toRupees(gridState.internalState.selectedAmount?.()!)} will
                  be generated{" "}
                </p>
              </Show>
              <Show when={internalState.dryRunErrorMessage() != null}>
                <div class="text-mediumBold text-red-700">
                  {internalState.dryRunErrorMessage()}
                </div>
              </Show>
            </Match>
          </Switch>
        </div>
        <Show
          when={props.offerConstruct}
          fallback={
            <DividerV2 class="bg-[#E5E5E5]  darkTheme:bg-baseTertiaryDark dark:bg-basePrimaryMedium" />
          }
        >
          <DividerWithOfferConstruct
            offerConstruct={props.offerConstruct}
            dividerClass={props.dividerClass}
          />
        </Show>
        <div class="flex w-full flex-col  gap-4 px-4 lg:px-6">
          {/* <MakePaymentCta /> */}
          <Show when={internalState.initialErrorOnSimulate()}>
            <div class="text-mediumBold text-red-700">
              {internalState.initialErrorOnSimulate()}
            </div>
          </Show>
          <Show
            when={
              props.allowRewardCoins &&
              props.coinsBalance > 0 &&
              !internalState.initialErrorOnSimulate()
            }
          >
            <CoinConsumptionInformation />
            <Divider />
          </Show>
          <Show
            when={
              props.showHubbleCoinRewards &&
              !internalState.initialErrorOnSimulate()
            }
          >
            <CoinEarningInformation />
            <Show when={props.isCoinsOnly || !props.isRewardTypePostPaid}>
              <Divider />
            </Show>
          </Show>
          <div class="flex flex-col gap-4 self-stretch ">
            <Switch>
              <Match
                when={
                  props.offerConstruct?.type === "SUBSCRIPTION_REWARD" &&
                  props.offerConstruct.subscriptionRewardDetails.length === 1
                }
              >
                <SingleSubscriptionInfo />
              </Match>
              <Match
                when={
                  props.offerConstruct?.type === "SUBSCRIPTION_REWARD" &&
                  props.offerConstruct.subscriptionRewardDetails.length > 1
                }
              >
                <MultipleSubscriptionInfo />
              </Match>
              <Match when={props.isCoinsOnly}>
                <Switch>
                  <Match when={gridState.enoughCoinsAvailable()}>
                    <Switch>
                      <Match
                        when={
                          internalState.dueAmount() ==
                          internalState.redemptionAmount()
                        }
                      >
                        <CoinsOnlyConsumptionInfo />
                      </Match>
                      <Match when={true}>
                        <DueAmount isCoinsOnly={true} />
                      </Match>
                    </Switch>
                  </Match>
                  <Match when={true}>
                    {props.buildInsufficientCoins?.() ?? (
                      <InsufficientCoins
                        coinsBalance={props.coinsBalance}
                        requiredBalance={internalState.dueAmount}
                      />
                    )}
                  </Match>
                </Switch>
              </Match>
              <Match
                when={
                  !props.isRewardTypePostPaid &&
                  !internalState.initialErrorOnSimulate()
                }
              >
                <DueAmount />
              </Match>
            </Switch>
            <div class="flex flex-col items-start justify-start gap-3 self-stretch ">
              <ProceedButton />
              <TransactionAdditionalInfo />
            </div>
          </div>
        </div>
      </div>
    </DenominationGridProvider>
  );
}
